import React, { useCallback, useEffect, useState } from "react";
import { addNotification as notify } from "reapop";
import { connect } from "react-redux";
import AdminNavigation from "../AdminNavigation";
import { TETContentManagementFields, TETContentUserTypeOptions } from "./Field";
import AdminConfirmationModal from "../adminConfirmationModal";
import { validateField } from "../../../pages/functions";
import Select from "../../../forms/Select";
import { api_v2 } from "../../NewIndex.api.js";
import "./Styles.css";
import Editor from "./Editor";
import { REF_DATA } from "../../../constants.js";
import { getApplicationRegex } from "../../plugins/utils.js";

const TETContentManagement = (props) => {
  const [countryOptions, setCountryOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [contentTypeOptions, setContentTypeOptions] = useState([]);
  const [showAdminConfirmationModal, setShowAdminConfirmationModal] =
    useState(false);
  const [flag, setFlag] = useState(false);
  const [text, setText] = useState("");
  const [initializetext, setInitializetext] = useState("");
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [fields, setFields] = useState(TETContentManagementFields);

  const validate = useCallback((e, field) => {
    const inputValue = e.target.value.toLowerCase();
    const ref_Data = props.referenceData.find(function (category) {
      return category.type === REF_DATA;
    }).referenceData;
    const regex = getApplicationRegex(ref_Data);

    if (regex.Emoji_Regex !== null && regex.Emoji_Regex.test(inputValue)) {
      // Emoji detected, prevent it from being added
      showNotification("Error!", "Special characters are not allowed", "error");
      return;
    }
    const validateFields = validateField(e, field, fields);
    setFields({ ...validateFields });
  }, []);

  const resetState = useCallback(() => {
    setDisableSubmit(true);
    setShowAdminConfirmationModal(false);
    Object.keys(TETContentManagementFields).forEach((key) => {
      TETContentManagementFields[key].value = "";
    });
    setFields({ ...TETContentManagementFields });
    setFlag(false);
  }, []);

  const getCountriesList = useCallback(() => {
    const countryOptions = [];
    api_v2.fetchTETCountries((JsonKeys) => {
      JsonKeys.forEach((countryList) => {
        countryOptions.push({
          value: countryList.country,
          label: countryList.country,
        });
      });
      setCountryOptions(countryOptions);
    });
  }, []);

  //language api
  const getLanguageList = useCallback(() => {
    const languageOptions = [];
    api_v2.fetchTETLanguage((JsonKeys) => {
      JsonKeys.forEach((languageList) => {
        languageOptions.push({
          value: languageList.code,
          label: languageList.language,
        });
      });
      setLanguageOptions(languageOptions);
    });
  }, []);

  const handleObserver = useCallback(() => {
    // Target node to observe
    const targetNode = document.getElementById("observeElement");

    // Options for the observer (which mutations to observe)
    const config = { childList: true, subtree: true };

    // Callback function to execute when mutations are observed
    const callback = function (mutationsList, observer) {
      for (const mutation of mutationsList) {
        if (mutation.type === "childList") {
          // Add further logic here
        }
      }
    };

    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(callback);

    // Start observing the target node for configured mutations
    if (targetNode) {
      observer.observe(targetNode, config);
    }

    // Cleanup when the component is unmounted or dependencies change
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    handleObserver();
    resetState();
    if (countryOptions.length === 0) {
      getCountriesList();
    }
    if (languageOptions.length === 0) {
      getLanguageList();
    }
    if (contentTypeOptions.length === 0) {
      getContentTypeList();
    }
  }, []);

  //content type API
  const getContentTypeList = useCallback(() => {
    const contentTypeOptions = [];
    api_v2.fetchTETContentType((contentTypes) => {
      contentTypes.forEach((contentType) => {
        contentTypeOptions.push({
          value: contentType,
          label: contentType,
        });
      });
      setContentTypeOptions(contentTypeOptions);
    });
  }, []);

  const handleTextChange = useCallback(
    (value) => {
      if (initializetext !== value) {
        setText(value);
        setDisableSubmit(false);
      } else setDisableSubmit(true);
    },
    [initializetext]
  );

  const handleSearch = useCallback(() => {
    setDisableSubmit(true);
    const payload = {
      country: fields.country.value,
      userType: fields.userType.value,
      language: fields.language.value,
      contentType: fields.content.value,
    };

    api_v2.fetchTETContent(payload, (res) => {
      if (res.length > 0) {
        const htmlContent = res[0].content;
        setInitializetext(htmlContent);
        setFlag(true);
        setText(htmlContent);
      } else {
        setFlag(false);
        setShowAdminConfirmationModal(true);
      }
    });
  }, [fields]);

  const UpdateTETContentHandler = useCallback(
    (res) => {
      if (res.status === 200) {
        showNotification(
          "Success",
          "Content is updated successfully",
          "success"
        );
        resetState();
        setFlag(false);
      } else {
        showNotification(
          "Error",
          "Something went wrong, Please try again later",
          "error"
        );
      }
    },
    [text]
  );

  const handleContentUpdate = useCallback(() => {
    const payload = {
      country: fields.country.value,
      userType: fields.userType.value,
      language: fields.language.value,
      contentType: fields.content.value,
      content: text,
    };

    api_v2.updateTETContent(payload, UpdateTETContentHandler);
  }, [fields, text]);

  const showNotification = (title, message, status) => {
    props.notify({
      title,
      message,
      status,
      position: "tr",
      dismissible: true,
      dismissAfter: 3000,
    });
  };
  const handleCancel = () => {
    setShowAdminConfirmationModal(false);
    resetState();
  };

  const createTETContent = useCallback(() => {
    const payload = {
      Country: fields.country.value,
      UserType: fields.userType.value,
      Language: fields.language.value,
      ContentType: fields.content.value,
      Content: "",
    };

    api_v2.createTETContent(payload, (res) => {
      if (res.ok && res.status === 200) {
        handleSearch();
        setShowAdminConfirmationModal(false);
        showNotification(
          "Success",
          "New content has created Successfully",
          "success"
        );
      } else if (res.status === 424) {
        showNotification(
          "Information",
          "This content already available",
          "info"
        );
      } else {
        showNotification(
          "Error",
          "Something went wrong, Please try again later",
          "error"
        );
      }
    });
  }, [fields]);

  const handleResetState = () => {
    setFlag(false);
    setText("");
  };

  return (
    <div id="manage-admin-list" className="v2_container">
      <div className="row">
        <AdminConfirmationModal
          isModalOpen={showAdminConfirmationModal}
          toggleYes={createTETContent}
          toggleCancel={handleCancel}
          bodyContent={
            "No Content available for the search values. Do you want to add a new content?"
          }
        />
        <AdminNavigation className="col-xl-2 col-lg-3 col-md-4 col-6" />
        <div className="col-xl-10 col-lg-9 col-md-8 col-6 p-l-15">
          <div className="row manage-admin-list-header">
            <div className="col-12 col-xl-12">
              <h3 className="mb-3 header-primary">Manage TET Content</h3>
            </div>
          </div>
          <div>
            <div className="row">
              <div className=" col-md-4">
                <Select
                  searchable
                  clearable={false}
                  options={countryOptions}
                  field={fields.country}
                  validate={(e) => validate(e, fields.country)}
                  onChange={handleResetState}
                />
              </div>
              <div className="col-md-4">
                <Select
                  searchable
                  clearable={false}
                  options={TETContentUserTypeOptions}
                  field={fields.userType}
                  validate={(e) => validate(e, fields.userType)}
                  onChange={handleResetState}
                />
              </div>
              <div className=" col-md-4">
                <Select
                  searchable
                  clearable={false}
                  options={languageOptions}
                  field={fields.language}
                  validate={(e) => validate(e, fields.language)}
                  onChange={handleResetState}
                />
              </div>
            </div>
            <div className="row">
              <div className=" col-md-4" style={{ marginTop: "10px" }}>
                <Select
                  searchable
                  clearable={false}
                  options={contentTypeOptions}
                  field={fields.content}
                  validate={(e) => validate(e, fields.content)}
                  onChange={handleResetState}
                />
                <br />
              </div>
              <div className="col-md-2">
                <button
                  className="btn btn-lg btn-info btn-rounded save_button"
                  onClick={handleSearch}
                  style={{ marginTop: "10px !important" }}
                  disabled={
                    fields.country.value === "" ||
                    fields.language.value === "" ||
                    fields.userType.value === "" ||
                    fields.content.value === ""
                  }
                >
                  Search
                </button>
              </div>
            </div>
            {flag && (
              <div style={{ marginTop: "10px" }}>
                <div className="row">
                  <div className="col -md-4">
                    <div className="table-container" id="observeElement">
                      <Editor
                        textState={text}
                        onTextChange={handleTextChange}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <button
                    className="btn btn-lg btn-info btn-rounded save_button"
                    onClick={() => handleContentUpdate()}
                    disabled={disableSubmit}
                  >
                    Save
                  </button>
                  <button
                    className="btn btn-lg btn-info btn-rounded"
                    onClick={() => resetState()}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    referenceData: state.referenceData
  };
}

export default connect(mapStateToProps, { notify })(TETContentManagement);



// WEBPACK FOOTER //
// ./src/mi-app/adminPanel/TET/V2TETContentManagement.js